import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Pencil, Plus } from "lucide-react";
import { AdminRole } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { createAdmin, listAdmins, updateAdmin } from "@/api/admins";
import { ApiError } from "@/api/client";
import { type AdminAccountResponse } from "@/api/types";
import { SearchableRows } from "@/components/table-search";

// Real implementation of Task 14e: list, create (dialog), and edit
// roles/active/password-reset (dialog). Entirely SUPERADMIN-gated — unlike
// Merchants there is no viewer/manager split, so (per _app.tsx's NAV_ITEMS)
// this route is unreachable for any non-SUPERADMIN admin in the first place;
// no further per-action role branching is needed inside the page itself.
// Replaces the Task 14a placeholder.
export const Route = createFileRoute("/_app/admins")({
  component: AdminsPage,
});

// All five roles, in the same declaration order as @cpg/common's AdminRole —
// used for both the checkbox-group options and the badge list rendering.
const ALL_ROLES: AdminRole[] = Object.values(AdminRole);

const ROLE_LABELS: Record<AdminRole, string> = {
  [AdminRole.SUPERADMIN]: "Superadmin",
  [AdminRole.MERCHANTS_VIEWER]: "Merchant viewer",
  [AdminRole.MERCHANTS_MANAGER]: "Merchant manager",
  [AdminRole.INVOICES_VIEWER]: "Invoice viewer",
  [AdminRole.INVOICES_MANAGER]: "Invoice manager",
  [AdminRole.AML_OFFICER]: "AML officer",
  [AdminRole.FINANCE_VIEWER]: "Finance viewer",
  [AdminRole.CONFIG_MANAGER]: "Configuration manager",
  [AdminRole.TECH_VIEWER]: "Technical viewer",
  [AdminRole.TECH_MANAGER]: "Technical manager",
  [AdminRole.CHANNELS_VIEWER]: "Channels viewer",
  [AdminRole.CHANNELS_MANAGER]: "Channels manager",
};

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// ─── pills / badges ──────────────────────────────────────────────────────────
// Same neutral-pill-with-colored-dot convention as _app.merchants.tsx's
// StatusPill/AccessPill.

function ActivePill({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-2.5 py-1 text-xs font-medium text-[#111827]">
      <span
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: active ? "#10B981" : "#EF4444" }}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// Roles are rendered as their own compact pill list (not a raw comma-joined
// string dump) — one small pill per role, wrapping onto multiple lines.
function RoleBadges({ roles }: { roles: AdminRole[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <span
          key={role}
          className="inline-flex items-center rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-medium text-[#3730A3]"
        >
          {ROLE_LABELS[role]}
        </span>
      ))}
    </div>
  );
}

// ─── role checkbox-group ────────────────────────────────────────────────────
//
// No Checkbox primitive exists in this app yet (unlike apps/merchant's
// Radix-backed one) and adding the @radix-ui/react-checkbox dependency for a
// single control isn't worth it here — a styled native <input
// type="checkbox"> reads clearly and needs no new package.

function RoleCheckboxGroup({
  idPrefix,
  selected,
  onChange,
  disabled,
}: {
  idPrefix: string;
  selected: AdminRole[];
  onChange: (next: AdminRole[]) => void;
  disabled?: boolean;
}) {
  const toggle = (role: AdminRole) => {
    onChange(selected.includes(role) ? selected.filter((r) => r !== role) : [...selected, role]);
  };

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {ALL_ROLES.map((role) => {
        const id = `${idPrefix}-${role}`;
        return (
          <label
            key={role}
            htmlFor={id}
            className="flex cursor-pointer items-center gap-2 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm text-[#111827] hover:bg-[#F9FAFB]"
          >
            <input
              id={id}
              type="checkbox"
              checked={selected.includes(role)}
              onChange={() => toggle(role)}
              disabled={disabled}
              className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A] disabled:cursor-not-allowed"
            />
            {ROLE_LABELS[role]}
          </label>
        );
      })}
    </div>
  );
}

const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";
const GUIDANCE_BOX = "rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-sm text-[#92400E]";

// ─── create admin dialog ────────────────────────────────────────────────────
//
// Controlled form matching _app.merchants.tsx's CreateMerchantDialog pattern
// (plain useState, useMutation, error banner). That form does no
// client-side password-strength validation and just surfaces the backend's
// 400 — matched here for consistency rather than inventing new rules. The
// role checkbox-group DOES get client-side non-empty validation: it's a
// UI-fully-controlled selection, so letting an officer submit zero roles and
// round-trip a 400 would be a pointless extra step.

function CreateAdminDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setDisplayName("");
    setEmail("");
    setPassword("");
    setRoles([]);
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: () => createAdmin({ displayName, email, password, roles }),
    onSuccess: () => {
      toast.success("Admin created.");
      queryClient.invalidateQueries({ queryKey: ["admins"] });
      setOpen(false);
      reset();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to create admin.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!displayName.trim() || !email.trim() || !password) {
      setError("All fields are required.");
      return;
    }
    if (roles.length === 0) {
      setError("Select at least one role.");
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Create admin
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create admin</DialogTitle>
          <DialogDescription>Creates a new backoffice admin account with the selected roles.</DialogDescription>
        </DialogHeader>

        {error && <div className={ERROR_BOX}>{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="displayName">Display name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <p className="text-xs text-[#6B7280]">
              At least 8 characters, with one digit and one special character.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Roles</Label>
            <RoleCheckboxGroup idPrefix="create" selected={roles} onChange={setRoles} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={createMutation.isPending} className="w-full sm:w-auto">
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create admin"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── edit admin dialog ───────────────────────────────────────────────────────
//
// Roles/active seed from the row already in the list (no GET /admins/:id
// endpoint exists — list is the only read). Password is a separate optional
// field: left blank, it's simply never included in the PATCH body ("don't
// change"); typed, it's sent verbatim. The last-active-superadmin 409's
// `message` is surfaced exactly as the backend wrote it (see
// admins.service.ts's `LAST_SUPERADMIN_MESSAGE`) — never paraphrased. A
// self-demotion courtesy warning (editing your OWN account so it would no
// longer be an active superadmin) is shown as non-blocking guidance: the
// backend only refuses when the target is the LAST active superadmin, and
// self-demotion while others remain is legitimately allowed.

interface EditAdminDialogProps {
  admin: AdminAccountResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAdminId: string;
}

function EditAdminDialog({ admin, open, onOpenChange, currentAdminId }: EditAdminDialogProps) {
  const queryClient = useQueryClient();
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && admin) {
      setRoles(admin.roles);
      setIsActive(admin.isActive);
      setPassword("");
      setError(null);
    }
  }, [open, admin]);

  const updateMutation = useMutation({
    mutationFn: () => {
      const body: { roles: AdminRole[]; isActive: boolean; password?: string } = { roles, isActive };
      if (password.length > 0) body.password = password;
      return updateAdmin(admin!.id, body);
    },
    onSuccess: () => {
      toast.success("Admin updated.");
      queryClient.invalidateQueries({ queryKey: ["admins"] });
      onOpenChange(false);
    },
    onError: (err) => {
      // Surfaced verbatim: the only 409 this route throws is the
      // last-active-superadmin rule, and its `message` already explains the
      // rule in plain language — no need (and no attempt) to branch on
      // `err.code` here the way _app.high-risk.tsx does for its multiple
      // distinct 409 codes.
      setError(err instanceof ApiError ? err.message : "Failed to update admin.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (roles.length === 0) {
      setError("Select at least one role.");
      return;
    }
    updateMutation.mutate();
  };

  const isSelf = admin?.id === currentAdminId;
  const wouldSelfDemote = isSelf && (!isActive || !roles.includes(AdminRole.SUPERADMIN));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {admin && (
          <>
            <DialogHeader>
              <DialogTitle>{admin.displayName}</DialogTitle>
              <DialogDescription>{admin.email}</DialogDescription>
            </DialogHeader>

            {error && <div className={ERROR_BOX}>{error}</div>}
            {!error && wouldSelfDemote && (
              <div className={GUIDANCE_BOX}>
                This is your own account — saving will remove your own superadmin access or sign-in
                ability (unless you are not the last active superadmin, in which case the backend
                still allows it).
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Roles</Label>
                <RoleCheckboxGroup
                  idPrefix="edit"
                  selected={roles}
                  onChange={setRoles}
                  disabled={updateMutation.isPending}
                />
              </div>

              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#E5E7EB] px-3 py-2 text-sm text-[#111827] hover:bg-[#F9FAFB]">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={updateMutation.isPending}
                  className="h-4 w-4 cursor-pointer rounded border-[#D1D5DB] accent-[#0F172A] disabled:cursor-not-allowed"
                />
                Active (can sign in)
              </label>

              <div className="space-y-1.5">
                <Label htmlFor="edit-password">New password (optional)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Leave blank to keep the current password"
                />
                <p className="text-xs text-[#6B7280]">
                  If set: at least 8 characters, with one digit and one special character.
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── admins page ─────────────────────────────────────────────────────────────

function AdminsPage() {
  const { admin: currentAdmin } = Route.useRouteContext();
  const [selected, setSelected] = useState<AdminAccountResponse | null>(null);

  const {
    data: admins,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["admins"],
    queryFn: listAdmins,
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <CreateAdminDialog />
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
        <SearchableRows rows={admins!}>
          {(shown) => (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F9FAFB] text-left text-xs font-semibold text-[#6B7280]">
                    <th className="px-5 py-3.5 font-semibold">Display name</th>
                    <th className="px-5 py-3.5 font-semibold">Email</th>
                    <th className="px-5 py-3.5 font-semibold">Roles</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold">Created</th>
                    <th className="px-5 py-3.5 font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {isPending ? (
                    <tr>
                      <td className="px-5 py-4 text-[#6B7280]" colSpan={6}>
                        Loading admins…
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td className="px-5 py-4 text-[#6B7280]" colSpan={6}>
                        Failed to load admins.
                      </td>
                    </tr>
                  ) : admins!.length === 0 ? (
                    <tr>
                      <td className="px-5 py-4 text-[#6B7280]" colSpan={6}>
                        No admins yet
                      </td>
                    </tr>
                  ) : (
                    shown.map((a) => (
                      <tr
                        key={a.id}
                        onClick={() => setSelected(a)}
                        className="cursor-pointer border-b border-[#F3F4F6] last:border-0 hover:bg-[#F9FAFB]"
                      >
                        <td className="px-5 py-3 font-medium text-[#111827]">{a.displayName}</td>
                        <td className="px-5 py-3 text-[#6B7280]">{a.email}</td>
                        <td className="px-5 py-3">
                          <RoleBadges roles={a.roles} />
                        </td>
                        <td className="px-5 py-3">
                          <ActivePill active={a.isActive} />
                        </td>
                        <td className="px-5 py-3 text-[#6B7280]">{formatTimestamp(a.createdAt)}</td>
                        <td className="px-5 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelected(a);
                            }}
                            title="Edit admin"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </SearchableRows>
      </div>

      <EditAdminDialog
        admin={selected}
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
        currentAdminId={currentAdmin.id}
      />
    </div>
  );
}
